import { api, BASE_URL } from './client';
import type { AccountBalanceDto } from '../types/report';

export interface VatSummaryDto {
  periodFrom: string;
  periodTo: string;
  totalSales: number;
  salesVat: number;
  totalPurchases: number;
  purchaseVat: number;
  netVatPayable: number;
}

export interface VatDeclarationDto {
  periodFrom: string;
  periodTo: string;
  totalSalesExcludingVat: number;
  salesVat21: number;
  salesVat9: number;
  salesVat5: number;
  salesVat0: number;
  totalSalesVat: number;
  totalPurchasesExcludingVat: number;
  purchaseVat21: number;
  purchaseVat9: number;
  purchaseVat5: number;
  totalPurchaseVat: number;
  netVatPayable: number;
}

export interface VatDeclarationLineDto {
  vatRate: number;
  taxableAmount: number;
  vatAmount: number;
}

export interface DebtAgingBucketDto {
  bucketName: string;
  daysFrom: number;
  daysTo: number;
  amount: number;
  personCount: number;
}

export interface DebtAgingPersonDto {
  person: {
    id: number;
    name: string;
    code: string;
  };
  totalOutstanding: number;
  currentAmount: number;
  bucket1to30: number;
  bucket31to60: number;
  bucket61to90: number;
  bucketOver90: number;
}

export interface DebtAgingDto {
  type: string;
  asOfDate: string;
  totalOutstanding: number;
  buckets: DebtAgingBucketDto[];
  personDetails: DebtAgingPersonDto[];
}

export interface IncomeStatementLineDto {
  accountNumber: number;
  accountName: string;
  amount: number;
}

export interface BalanceSheetLineDto {
  accountNumber: number;
  accountName: string;
  debitBalance: number;
  creditBalance: number;
  netBalance: number;
}

export interface BalanceSheetSectionDto {
  sectionName: string;
  classCode: string;
  total: number;
  lines: BalanceSheetLineDto[];
}

export interface FinancialStatementsDto {
  incomeStatement: {
    periodFrom: string;
    periodTo: string;
    totalIncome: number;
    totalExpenses: number;
    netProfit: number;
    incomeLines: IncomeStatementLineDto[];
    expenseLines: IncomeStatementLineDto[];
  };
  balanceSheet: {
    asOfDate: string;
    totalAssets: number;
    totalEquityAndLiabilities: number;
    sections: BalanceSheetSectionDto[];
  };
}

export const reportsApi = {
  trialBalance: (params: { from: string; to: string; account?: number; nonZeroOnly?: boolean }) =>
    api.get<AccountBalanceDto[]>('/reports/trial-balance', {
      from: params.from,
      to: params.to,
      account: params.account,
      nonZeroOnly: params.nonZeroOnly ?? true,
    }),

  vatSummary: (params: { from: string; to: string }) =>
    api.get<VatSummaryDto>('/reports/vat-summary', {
      from: params.from,
      to: params.to,
    }),

  vatDeclaration: (params: { from: string; to: string }) =>
    api.get<VatDeclarationDto>('/reports/vat-declaration', {
      from: params.from,
      to: params.to,
    }),

  debtAging: (params: { asOf: string; type?: string }) =>
    api.get<DebtAgingDto>('/reports/debt-aging', {
      asOf: params.asOf,
      type: params.type ?? 'receivable',
    }),

  financialStatements: (params: { from: string; to: string }) =>
    api.get<FinancialStatementsDto>('/reports/financial-statements', {
      from: params.from,
      to: params.to,
    }),

  exportSaft: (params: { format?: string; from?: string; to?: string }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('format', params.format ?? 'SAFT2.0');
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    window.open(`${BASE_URL}/reports/export?${searchParams.toString()}`, '_blank');
  },
};
